import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import { v4 as uuid } from 'uuid'

import { getDefaultValues } from '@pages/DiagramsCreateOrEdit/helpers'
import {
  convertDataToRequestData,
  convertResponseToData,
} from '@pages/DiagramsCreateOrEdit/helpers/dataConverter'
import { SeriesType } from '@pages/DiagramsCreateOrEdit/types'

import { useFetchAllObjectQuery, useFetchAllViewQuery } from '@redux/api'
import {
  useCreateOrUpdateBarChartMutation,
  useFetchBarChartByIdMutation,
} from '@redux/api/barChart.api'

import { usePrompt } from '@hooks'
import { DIAGRAMS_TABS, INCREMENT_TYPE, PERIOD_TYPE, ROUTES } from '@constants'
import { AutocompleteOption } from '@types'

import {
  BarChartResponseType,
  ColumnRequestType,
  DiagramRequestType,
  OBJECT_TYPE,
  OPERATION_TYPE,
  ResourceRequestType,
  SeriesRequestType,
  TimelineRequestType,
} from '../../../types/api/barChart.api'

export const useHandlers = () => {
  const { t } = useTranslation()
  const { id: diagramId } = useParams()
  const navigate = useNavigate()
  const defaultFormValues = getDefaultValues()
  const methods = useForm({ defaultValues: defaultFormValues })
  const {
    watch,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { isDirty, dirtyFields, defaultValues },
    reset,
    getValues,
  } = methods

  usePrompt({ when: isDirty })

  const watchResources = watch('resources')
  const watchTimeline = watch('timeline')
  const watchSeries = watch('timeline.series')
  const watchColumns = watch('columns')
  const watchId = watch('id')

  const { data: objects } = useFetchAllObjectQuery()
  const { data: views } = useFetchAllViewQuery()

  const [currentTab, setCurrentTab] = useState(DIAGRAMS_TABS.AXISX)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [editColumnId, setEditColumnId] = useState<number>()

  const maxSeriesLength = useMemo(() => {
    switch (watchTimeline.period.id) {
      default:
      case PERIOD_TYPE.DAY:
        return 24
      case PERIOD_TYPE.WEEK:
        return 7
      case PERIOD_TYPE.MONTH:
        return 31
      case PERIOD_TYPE.YEAR:
        return 12
    }
  }, [watchTimeline.period.id])

  const editColumnData = useMemo(
    () => watchColumns.find(({ id }) => id === editColumnId),
    [editColumnId]
  )

  const objectsOptions: AutocompleteOption[] = useMemo(
    () => objects?.map(obj => ({ id: obj.code, label: obj.title })) || [],
    [objects]
  )

  const viewsOptions: AutocompleteOption[][] = useMemo(() => {
    return watchColumns.map(column => {
      const filteredViews = views?.data?.filter(view => view.objectCode === column.objectCode) || []

      return filteredViews.map(view => ({ id: view.id, label: view.title }))
    })
  }, [views, watchColumns])

  const handleViewChange = (index: number, value: AutocompleteOption, columnId: number) => {
    setValue(`resources.${index}.columnId`, columnId)
    setValue(`resources.${index}.viewTitle`, value?.label)
    setValue(`resources.${index}.viewId`, value?.id, { shouldDirty: true })
  }

  // const handleViewChange = (index: number) => {
  //   if (!watchResources[index].object) {
  //     setValue(
  //       `resources.${index}.object`,
  //       objectsOptions.find(({ id }) => id === watchResources[1].view?.id)
  //     )
  //   }
  // }

  const [requestBarChart, { isLoading: isLoadingCreate }] = useCreateOrUpdateBarChartMutation()
  const [fetchBarChartById] = useFetchBarChartByIdMutation()

  useEffect(() => {
    if (diagramId && views?.data) {
      fetchBarChartById({ id: diagramId }).then(resp => {
        if (resp.data) {
          updateDiagramData(resp.data)
        }
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [diagramId, views?.data])

  const handleChangeTab = (_: SyntheticEvent<Element, Event>, value: number) => {
    if (!isDirty) {
      setCurrentTab(value)

      return
    }
    if (confirm(t('notifications.leave'))) {
      setCurrentTab(value)
      reset()
    }
  }

  const handleAddColumn = () => {
    setEditColumnId(undefined)
    setShowAddDialog(true)
  }

  const handleEditColumn = (id: number) => {
    setEditColumnId(id)
    setShowAddDialog(true)
  }

  const handleDeleteColumn = (id: number) => {
    requestBarChart([
      {
        operationType: OPERATION_TYPE.DELETE,
        type: OBJECT_TYPE.COLUMN,
        data: {
          diagramId: watchId,
          id,
        },
      },
    ]).then(resp => {
      if (resp.data) {
        setValue(
          'columns',
          watchColumns.filter(({ id: sId }) => sId !== id),
          { shouldDirty: false }
        )
      }
    })
  }

  const onCloseModal = (isDirty: boolean) => {
    if (!isDirty) {
      setShowAddDialog(false)
      setEditColumnId(undefined)

      return
    }
    if (confirm(t('notifications.leave'))) {
      setShowAddDialog(false)
      setEditColumnId(undefined)
    }
  }

  const updateSeriesRow = (value: SeriesType) => {
    const seriesIndex = watchSeries.findIndex(series => series.id === value.id)
    setValue(`timeline.series.${seriesIndex}`, value, { shouldDirty: true })
  }

  const handleDeleteSeries = (id: number, row) => {
    if (id) {
      const newSeries = [...watchSeries]
      newSeries.splice(
        newSeries.findIndex(series => series.id === id),
        1
      )
      setValue('timeline.series', newSeries, { shouldDirty: true })
    }
  }

  const handleDragEnd = (array, type: OBJECT_TYPE) => {
    const changeOrderRequests: ColumnRequestType = array.map((data, index) => {
      return {
        operationType: OPERATION_TYPE.UPDATE,
        type,
        data: {
          ...data,
          order: index,
          diagramId: type !== OBJECT_TYPE.SERIES ? watchId : undefined,
          timelineId: type !== OBJECT_TYPE.SERIES ? undefined : watchTimeline.id,
          internalId: undefined,
        },
      }
    })
    requestBarChart(changeOrderRequests).then(resp => {
      if (resp.data) {
        updateDiagramData(resp.data)
      }
    })

    return array
  }

  const handleSaveDiagram = handleSubmit(data => {
    clearErrors()
    const requestArray = []
    const requestData = convertDataToRequestData(data)

    if (data.id) {
      if (dirtyFields.title || dirtyFields.code) {
        const updateDiagramRequest: DiagramRequestType = {
          operationType: OPERATION_TYPE.UPDATE,
          type: OBJECT_TYPE.DIAGRAM,
          data: {
            id: requestData.id,
            code: requestData.code,
            title: requestData.title,
          },
        }
        requestArray.push(updateDiagramRequest)
      }
      if (dirtyFields.timeline) {
        const updateTimelineRequest: TimelineRequestType = {
          operationType: requestData.timeline.id ? OPERATION_TYPE.UPDATE : OPERATION_TYPE.CREATE,
          type: OBJECT_TYPE.TIMELINE,
          data: requestData.timeline,
        }
        requestArray.push(updateTimelineRequest)
        if (dirtyFields.timeline.series) {
          const updateSeriesRequests: SeriesRequestType[] = requestData.series.map(
            singleSeries => ({
              operationType: singleSeries.id?.toString().includes('tempSeriesId')
                ? OPERATION_TYPE.CREATE
                : OPERATION_TYPE.UPDATE,
              type: OBJECT_TYPE.SERIES,
              data: {
                ...singleSeries,
                id: singleSeries.id?.toString().includes('tempSeriesId')
                  ? undefined
                  : singleSeries.id,
              },
            })
          )
          const deleteSeriesRequests: SeriesRequestType[] = defaultValues.timeline.series
            .filter(defaultSeries => !requestData.series.some(({ id }) => id === defaultSeries.id))
            .map(singleSeries => ({
              operationType: OPERATION_TYPE.DELETE,
              type: OBJECT_TYPE.SERIES,
              data: {
                id: singleSeries.id,
                timelineId: requestData.timeline.id,
              },
            }))
          requestArray.push(...updateSeriesRequests, ...deleteSeriesRequests)
        }
      }
      if (dirtyFields.resources) {
        const updateResourcesRequest: ResourceRequestType[] = requestData.resources
          .filter(res => Boolean(res.columnId))
          .map(resource => ({
            operationType: resource.id ? OPERATION_TYPE.UPDATE : OPERATION_TYPE.CREATE,
            type: OBJECT_TYPE.RESOURCE,
            data: { ...resource, diagramId: watchId, viewTitle: undefined },
          }))
        requestArray.push(...updateResourcesRequest)
      }
    } else if (requestData.code && requestData.title) {
      const createDiagramRequest: DiagramRequestType = {
        operationType: OPERATION_TYPE.CREATE,
        type: OBJECT_TYPE.DIAGRAM,
        data: {
          code: requestData.code,
          title: requestData.title,
        },
      }
      const createTimelineRequest: TimelineRequestType = {
        operationType: OPERATION_TYPE.CREATE,
        type: OBJECT_TYPE.TIMELINE,
        data: requestData.timeline,
      }
      const createSeriesRequests: SeriesRequestType[] = requestData.series.map(singleSeries => ({
        operationType: OPERATION_TYPE.CREATE,
        type: OBJECT_TYPE.SERIES,
        data: singleSeries,
      }))
      requestArray.push(createDiagramRequest, createTimelineRequest, ...createSeriesRequests)
    }
    if (requestArray.length) {
      requestBarChart(requestArray).then(resp => {
        if (resp.data) {
          updateDiagramData(resp.data)
        }
      })
    }
  })

  const updateDiagramData = (data: BarChartResponseType) => {
    const newData = convertResponseToData(data, views.data)
    reset(newData)
  }

  const onShowDialog = () => {
    setShowAddDialog(true)
  }

  const handleCancelDiagram = () => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    reset()

    navigate(ROUTES.DIAGRAMS)
  }

  const globalDirty = useMemo(() => isDirty, [isDirty])

  const handleChangeSeries = () => {
    if (!watchTimeline.dateRange[0] || !watchTimeline.dateRange[1]) {
      return
    }

    const startDate = moment(watchTimeline.dateRange[0])
    const endDate = moment(watchTimeline.dateRange[1])

    let diffUnit: moment.unitOfTime.Diff
    switch (watchTimeline.timeIncrement.id) {
      case INCREMENT_TYPE.MINUTE:
        diffUnit = 'minutes'
        break
      case INCREMENT_TYPE.HOUR:
        diffUnit = 'hours'
        break
      case INCREMENT_TYPE.DAY:
        diffUnit = 'days'
        break
      case INCREMENT_TYPE.WEEK:
        diffUnit = 'weeks'
        break
      case INCREMENT_TYPE.MONTH:
        diffUnit = 'months'
        break
      default:
        diffUnit = 'days'
    }

    const diff = endDate.diff(startDate, diffUnit)
    if (diff <= 0) {
      return
    }

    const newSeries = Array.from({ length: diff }, (_, index) => {
      const fromDate = moment(startDate).add(index, diffUnit)
      const tillDate = moment(startDate).add(index + 1, diffUnit)

      const title = getSeriesLabel(fromDate, tillDate)

      return {
        id: 'tempSeriesId' + uuid(),
        title,
        order: index,
        dateRange: { from: index, till: index + 1 },
      }
    })
    setValue('timeline.series', newSeries, { shouldDirty: true })
  }

  const getSeriesLabel = (from: moment.Moment, till: moment.Moment) => {
    switch (watchTimeline.timeIncrement.id) {
      case INCREMENT_TYPE.MINUTE:
        return `${from.format('HH:mm')}-${till.format('HH:mm')}`
      case INCREMENT_TYPE.HOUR:
        return `${from.format('DD.MM HH:00')}-${till.format('HH:00')}`
      case INCREMENT_TYPE.DAY:
        return from.format('DD.MM.YYYY')
      case INCREMENT_TYPE.WEEK:
        return `${from.format('DD.MM.YYYY')}-${till.format('DD.MM.YYYY')}`
      case INCREMENT_TYPE.MONTH:
        return from.format('MM.YYYY')
      default:
        return from.format('DD.MM.YYYY')
    }
  }

  const handleAddSeries = () => {
    let diffUnit: moment.unitOfTime.DurationConstructor
    switch (watchTimeline.timeIncrement.id) {
      case INCREMENT_TYPE.MINUTE:
        diffUnit = 'minutes'
        break
      case INCREMENT_TYPE.HOUR:
        diffUnit = 'hours'
        break
      case INCREMENT_TYPE.DAY:
        diffUnit = 'days'
        break
      case INCREMENT_TYPE.WEEK:
        diffUnit = 'weeks'
        break
      case INCREMENT_TYPE.MONTH:
        diffUnit = 'months'
        break
      default:
        diffUnit = 'days'
    }

    const lastSeries = watchSeries.length > 0 ? watchSeries[watchSeries.length - 1] : undefined
    const from = isFinite(Number(lastSeries?.dateRange?.till))
      ? Number(lastSeries.dateRange.till)
      : 0
    const till = from + 1
    const fromDate = moment(watchTimeline.dateRange[0]).add(from, diffUnit)
    const tillDate = moment(watchTimeline.dateRange[0]).add(from, diffUnit)

    const newSeries = {
      id: 'tempSeriesId' + uuid(),
      timelineId: watchTimeline.id,
      title: getSeriesLabel(fromDate, tillDate),
      order: watchSeries.length,
      dateRange: {
        from,
        till,
      },
    }

    setValue('timeline.series', [...watchSeries, newSeries], { shouldDirty: true })
  }

  return {
    data: {
      objects,
      series: watchSeries,
      columns: watchColumns,
    },
    state: {
      isLoadingCreate,
      isLoading,
      methods,
      maxSeriesLength,
      currentTab,
      showAddDialog,
      objectsOptions,
      viewsOptions,
      globalDirty,
      editColumnId,
      editColumnData,
    },
    handlers: {
      handleChangeSeries,
      handleAddSeries,
      handleChangeTab,
      handleSaveDiagram,
      handleCancelDiagram,
      handleViewChange,
      handleAddColumn,
      handleEditColumn,
      handleDeleteColumn,
      handleDeleteSeries,
      onShowDialog,
      onCloseModal,
      handleDragEnd,
      updateSeriesRow,
      updateDiagramData,
    },
  }
}
